import { useCurrentUser } from '@ianesthesie-kit/redux';
import { useCallback } from 'react';
import { HospitalService, useHospitalByIdQuery } from '@/modules/hospital';
import {
  selectCurrentHospital,
  setCurrentHospital
} from '@/modules/appContext';
import { type HospitalConfig } from '@ianesthesie-kit/services';
import { useAppDispatch, useAppSelector } from '@/hooks';

export const useCurrentHospital = (opts?: { skip?: boolean }) => {
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const jwtHospitalId = useAppSelector(selectCurrentHospital);

  const { refetch: rtkRefetch, ...queryResult } = useHospitalByIdQuery(
    jwtHospitalId ?? '',
    {
      skip: !jwtHospitalId || !user || !!opts?.skip
    }
  );

  const setHospitalIdFromToken = useCallback(() => {
    dispatch(
      setCurrentHospital(HospitalService.getInstance().getHospitalIdFromJwt())
    );
  }, [dispatch]);

  const refetch = useCallback(() => {
    setHospitalIdFromToken();
    if (!queryResult.isUninitialized) {
      return rtkRefetch();
    }
    return undefined;
  }, [setHospitalIdFromToken, queryResult.isUninitialized, rtkRefetch]);

  // `isFetching && !currentData` rather than `isFetching`: a background refetch of the same
  // hospital (Hospital-tag invalidation) keeps `currentData`, so consumers gating on this flag
  // don't unmount. An active-hospital switch changes the arg, drops `currentData` and stays
  // unstable until the new hospital is loaded.
  const isUnstable =
    !jwtHospitalId ||
    queryResult.isUninitialized ||
    (queryResult.isFetching && !queryResult.currentData);

  return {
    refresh: setHospitalIdFromToken,
    isUnstable,
    queryResult: {
      ...queryResult,
      refetch
    }
  };
};

export const useCurrentHospitalCountry = () =>
  useCurrentHospital()?.queryResult?.currentData?.country;

type ModuleKey = OmitString<
  Extract<keyof HospitalConfig, `${string}Module`>,
  'Module'
>;
type OmitString<
  T extends string,
  O extends string
> = T extends `${infer P}${O}${infer S}` ? `${P}${S}` : T;

export const useIsHospitalModuleEnabled = (
  moduleKey: ModuleKey,
  opts?: { skip?: boolean }
) => {
  const key = `${moduleKey}ModuleEnabled` as const;
  return !!useCurrentHospital(opts)?.queryResult?.currentData?.[key];
};

export const useIsUsapHospital = () =>
  HospitalService.isUsapHospital(
    useCurrentHospital()?.queryResult?.currentData
  );
